export default class BaseFilterController {
    constructor(section, filterMap = {}) {
        this.section = section;
        this.config = this.getConfig();
        this.state = {
            currentPage: 1,
            maxNumPages: this.config.initialMaxPages,
        };
        this.filters = {}; // Store all active filters
        this.filterMap = filterMap; // Filter schema (defined in child class)

        this.initFilters();
    }

    /** Read configuration from data attributes */
    getConfig() {
        const d = this.section.dataset;
        return {
            postType: d.postType || 'post',
            postsPerPage: parseInt(d.postsPerPage) || 2,
            order: d.order || 'DESC',
            endpoint: d.endpoint || '/wp-json/api/posts_filtration',
            initialMaxPages: parseInt(d.maxNumPages) || 1,
            buttonText: d.buttonText || '',
        };
    }

    /** Connect filters from filterMap */
    initFilters() {
        Object.entries(this.filterMap).forEach(([key, options]) => {
            const element = this.section.querySelector(options.selector);
            if (!element) return;

            // Callback for filter change
            const handler = (value) => {
                this.updateFilterValue(key, value);
                this.state.currentPage = 1;
                this.fetchResults();
            };

            this.bindFilter(element, options, handler);
        });

        // Clear all filters buttons (may be multiple)
        this.section.querySelectorAll('[data-clear-filters-js]').forEach((btn) => {
            btn.addEventListener('click', () => this.clearAllFilters());
        });

        // Load more button
        const loadMoreBtn = this.section.querySelector('[data-load-more-js]');
        if (loadMoreBtn) {
            loadMoreBtn.addEventListener('click', () => {
                if (this.state.currentPage < this.state.maxNumPages) {
                    this.state.currentPage++;
                    this.fetchResults(true);
                }
            });
        }

        // Update load more button visibility on init
        this.updateLoadMoreButton();

        // Numbered pagination
        this.initPagination();
        this.renderPagination();
    }

    /** Universal binding of filters with events */
    bindFilter(element, options, handler) {
        const { type } = options;

        switch (type) {
            case 'customSelect':
                element.addEventListener('change', (e) => handler(e.detail?.value || e.target.value));
                break;

            case 'radio':
            case 'checkbox':
                element.addEventListener('change', (e) => handler(e.target.value));
                break;

            case 'input':
                element.addEventListener('input', (e) => handler(e.target.value));
                break;

            case 'checkboxGroup':
                element.addEventListener('change', (e) => {
                    if (!e.target.matches('input[type="checkbox"]')) return;
                    const checked = [...element.querySelectorAll('input[type="checkbox"]:checked')]
                        .map(cb => cb.value)
                        .filter(Boolean);
                    handler(checked);
                });
                break;

            case 'buttonGroup':
                element.addEventListener('click', (e) => {
                    const btn = e.target.closest('[data-value]');
                    if (!btn) return;
                    handler(btn.dataset.value);
                });
                break;

            default:
                console.warn(`[FilterController] Unknown filter type: ${type}`);
        }
    }

    /** Update internal filter state */
    updateFilterValue(key, value) {
        this.filters[key] = value;
    }

    /** Perform REST request */
    async fetchResults(append = false) {
        this.section.classList.add('loading');

        try {
            const body = new URLSearchParams({
                postType: this.config.postType,
                postsPerPage: this.config.postsPerPage,
                order: this.config.order,
                paged: this.state.currentPage,
                filters: JSON.stringify(this.filters),
                contentType: this.config.postType,
                buttonText: this.config.buttonText,
            });

            const res = await fetch(this.config.endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body,
            });

            const data = await res.json();
            this.renderResults(data, append);
        } catch (err) {
            console.error('Filter fetch error:', err);
        } finally {
            this.section.classList.remove('loading');
        }
    }

    /** Initialize numbered pagination (click delegation on [data-pagination-js]) */
    initPagination() {
        const container = this.section.querySelector('[data-pagination-js]');
        if (!container) return;

        container.addEventListener('click', (e) => {
            const btn = e.target.closest('[data-page]');
            if (!btn || btn.hasAttribute('disabled')) return;

            const page = parseInt(btn.dataset.page, 10);
            if (page < 1 || page > this.state.maxNumPages) return;

            this.state.currentPage = page;
            this.fetchResults();

            // Scroll section into view
            this.section.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
    }

    /** Render numbered pagination inside [data-pagination-js] */
    renderPagination() {
        const container = this.section.querySelector('[data-pagination-js]');
        if (!container) return;

        const { currentPage: cur, maxNumPages: max } = this.state;

        if (max <= 1) {
            container.innerHTML = '';
            return;
        }

        let html = '';

        const svgArrow = `<svg class="sk-btn__icon sk-btn__icon--arrow-right " fill="none" xmlns="http://www.w3.org/2000/svg"><use xlink:href="#icon-arrow-right"></use></svg>`;

        // Prev button
        html += `<button class="pagination__btn pagination__btn--prev" aria-label="Previous page" data-page="${cur - 1}"${cur <= 1 ? ' disabled' : ''}><span style="display:inline-flex;transform:rotate(180deg)">${svgArrow}</span></button>`;

        // Page numbers
        for (let i = 1; i <= max; i++) {
            html += `<button class="pagination__btn${i === cur ? ' is-active' : ''}" data-page="${i}">${i}</button>`;
        }

        // Next button (same icon rotated 180deg)
        html += `<button class="pagination__btn pagination__btn--next" aria-label="Next page" data-page="${cur + 1}"${cur >= max ? ' disabled' : ''}><span>${svgArrow}</span></button>`;

        container.innerHTML = html;
    }

    /** Render active filters summary and manage filter-meta visibility */
    renderActiveFilters() {
        const filterMeta  = this.section.querySelector('[data-filter-meta-js]');
        const container   = this.section.querySelector('[data-active-filters-js]');
        const clearBtns   = this.section.querySelectorAll('[data-clear-filters-js]');

        // Collect active filter labels
        const labels = [];

        Object.entries(this.filterMap).forEach(([key, options]) => {
            if (options.type !== 'checkboxGroup') return;
            const values = this.filters[key];
            if (!values || !values.length) return;

            const dropdownEl = this.section.querySelector(options.selector);
            if (!dropdownEl) return;

            values.forEach((val) => {
                const cb = dropdownEl.querySelector(`input[value="${val}"]`);
                const label = cb?.closest('label')?.querySelector('.label')?.textContent?.trim();
                if (label) labels.push(label);
            });
        });

        const hasFilters = labels.length > 0;

        // Show/hide the whole filter-meta block
        if (filterMeta) {
            filterMeta.style.display = hasFilters ? '' : 'none';
        }

        // Show/hide all "Clear all" buttons — works independently of [data-active-filters-js]
        clearBtns.forEach((btn) => {
            btn.style.display = hasFilters ? '' : 'none';
        });

        // Update active filters text (optional element)
        if (!container) return;

        container.innerHTML = hasFilters
            ? `<span>Filter by:</span> ${labels.join(', ')}`
            : '';
    }

    /** Reset all active filters and re-fetch results */
    clearAllFilters() {
        // Clear internal state
        this.filters = {};
        this.state.currentPage = 1;

        // Uncheck all checkboxes in filter dropdowns
        Object.values(this.filterMap).forEach((options) => {
            const element = this.section.querySelector(options.selector);
            if (!element) return;
            element.querySelectorAll('input[type="checkbox"]:checked').forEach((cb) => {
                cb.checked = false;
                // Dispatch change event so dropdown component can update its UI
                cb.dispatchEvent(new Event('change', { bubbles: true }));
            });
        });

        this.fetchResults();
        this.renderActiveFilters();
    }

    /** Update posts found counter (shown only when filters are active — parent visibility controls this) */
    renderPostsCount(total) {
        const el = this.section.querySelector('[data-posts-count-js]');
        if (!el) return;
        el.textContent = total > 0 ? `${total} posts found` : '';
    }

    /** Show/hide "no posts found" message */
    renderNoPostsMessage(html) {
        const noPostsEl = this.section.querySelector('[data-no-posts-js]');
        if (!noPostsEl) return;
        noPostsEl.style.display = !html?.trim() ? 'block' : 'none';
    }

    /** Render results */
    renderResults(response, append) {
        const grid = this.section.querySelector('[data-grid-js]');
        if (!grid) return;

        this.state.maxNumPages = response.maxNumPages ?? 1;

        if (!append) grid.innerHTML = '';
        grid.insertAdjacentHTML('beforeend', response.html);

        this.updateLoadMoreButton();
        this.renderPagination();
        this.renderActiveFilters();
        this.renderPostsCount(response.totalFound ?? 0);
        this.renderNoPostsMessage(response.html);
    }

    /** Update load more button visibility */
    updateLoadMoreButton() {
        const loadMoreBtn = this.section.querySelector('[data-load-more-js]');
        if (loadMoreBtn) {
            loadMoreBtn.style.display =
                this.state.currentPage >= this.state.maxNumPages ? 'none' : 'flex';
        }
    }
}
